import { mobileShopApi } from '../mobileShopApi';
import { MasterRow, PagedResponse } from '../../types/master';
import { ImeiStock, Product, ProductBatch } from '../../types/product';

/**
 * Products, batches and handsets.
 *
 * <p>Both ways of finding something are here on purpose: `quickSearch` for the one
 * box the counter types into, `byModel` for walking brand to model to product. The
 * billing screen offers both at once.
 */
export const productApi = mobileShopApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * The product list. searchValue is matched word by word on the server, so the
     * words can be typed in any order; `properties` pins down any number of exact
     * properties - the built-in ones and whatever specifications the shop records.
     */
    getProductPage: builder.mutation<
      PagedResponse<MasterRow>,
      {
        searchValue: string;
        brandId?: number | null;
        modelId?: number | null;
        properties?: Record<string, string>;
        page: number;
        size: number;
      }
    >({
      query: ({ searchValue, brandId, modelId, properties, page, size }) => ({
        url: `/product/search-by-criteria/page=${page}/size=${size}`,
        method: 'POST',
        body: { searchValue, brandId, modelId, properties: properties ?? {} },
      }),
    }),

    /**
     * What the product screen may filter by. Asked for rather than listed in the code,
     * so a specification the shop starts recording turns up here on its own.
     */
    getFilterProperties: builder.query<{ key: string; label: string }[], void>({
      query: () => '/product/filter-properties',
      providesTags: ['Product'],
    }),

    getProductById: builder.query<Product, number>({
      query: (id) => `/product/${id}`,
      providesTags: ['Product'],
    }),

    /** The type-ahead: name, barcode, brand or model all match. */
    searchProducts: builder.query<Product[], string>({
      query: (tag) => `/product/quick-search?tag=${encodeURIComponent(tag)}`,
      providesTags: ['Product'],
    }),

    /** The sequential path: everything under one model. */
    getProductsByModel: builder.query<Product[], number>({
      query: (modelId) => `/product/model/${modelId}`,
      providesTags: ['Product'],
    }),

    findProductByBarcode: builder.query<Product, string>({
      query: (barcode) => `/product/barcode/${encodeURIComponent(barcode)}`,
    }),

    /** Asked while the shop is still typing, so a clash is shown before they save. */
    checkProductDuplicate: builder.mutation<{ duplicate: boolean }, Partial<Product>>({
      query: (body) => ({ url: '/product/duplicate', method: 'POST', body }),
    }),

    /** Consignments still holding stock, oldest first. */
    getOpenBatches: builder.query<ProductBatch[], number>({
      query: (productId) => `/product/${productId}/batches`,
      providesTags: ['Batch'],
    }),

    getAllBatches: builder.query<ProductBatch[], number>({
      query: (productId) => `/product/${productId}/all-batches`,
      providesTags: ['Batch'],
    }),

    saveProduct: builder.mutation<Product, Product>({
      query: (body) => ({ url: '/product', method: 'POST', body }),
      invalidatesTags: ['Product'],
    }),

    updateProduct: builder.mutation<Product, Product>({
      query: (body) => ({ url: '/product', method: 'PUT', body }),
      invalidatesTags: ['Product'],
    }),

    deleteProduct: builder.mutation<void, number>({
      query: (id) => ({ url: `/product/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Product'],
    }),

    /* handsets */

    getAvailableImeisByProduct: builder.query<ImeiStock[], number>({
      query: (productId) => `/imei-stock/product/${productId}/available`,
      providesTags: ['Imei'],
    }),

    getAvailableImeisByBatch: builder.query<ImeiStock[], number>({
      query: (batchId) => `/imei-stock/batch/${batchId}/available`,
      providesTags: ['Imei'],
    }),

    findByImei: builder.query<ImeiStock, string>({
      query: (imeiNo) => `/imei-stock/imei/${imeiNo}`,
      providesTags: ['Imei'],
    }),

    getStockPage: builder.mutation<PagedResponse<MasterRow>, { searchValue: string; status: string; page: number; size: number }>({
      query: ({ searchValue, status, page, size }) => ({
        url: `/imei-stock/search-by-criteria/page=${page}/size=${size}`,
        method: 'POST',
        body: { searchValue, status },
      }),
    }),
  }),
});

export const {
  useGetProductPageMutation,
  useGetFilterPropertiesQuery,
  useLazyGetProductByIdQuery,
  useLazySearchProductsQuery,
  useGetProductsByModelQuery,
  useLazyFindProductByBarcodeQuery,
  useCheckProductDuplicateMutation,
  useGetOpenBatchesQuery,
  useGetAllBatchesQuery,
  useSaveProductMutation,
  useUpdateProductMutation,
  useDeleteProductMutation,
  useGetAvailableImeisByProductQuery,
  useGetAvailableImeisByBatchQuery,
  useLazyFindByImeiQuery,
  useGetStockPageMutation,
} = productApi;
